import { View, Text } from '@react-pdf/renderer';
import { styles } from './PDFStyles';
import type { Database } from '@/lib/database.types';

type Channel = Database['public']['Tables']['channels']['Row'] & {
  channel_tag_assignments?: Array<{
    tag?: Database['public']['Tables']['channel_tags']['Row'] | null;
  }>;
};

interface Tag {
  id: number;
  name: string;
  description?: string | null;
}

interface PDFTagsSectionProps {
  tags: Tag[];
  channels: Channel[];
  filterOptions?: {
    showTagDescription?: boolean;
    showTagChannelCount?: boolean;
  };
}

export function PDFTagsSection({ tags, channels, filterOptions = {} }: PDFTagsSectionProps) {
  const {
    showTagDescription = true,
    showTagChannelCount = true,
  } = filterOptions;

  if (!tags || tags.length === 0) {
    return null;
  }

  console.log('📋 PDFTagsSection: Rendering', tags.length, 'tags');

  // Calculate channel count for each tag
  const tagChannelCounts = tags.map(tag => {
    const channelCount = channels.filter(channel =>
      channel.channel_tag_assignments?.some(cta => cta.tag?.id === tag.id)
    ).length;

    return {
      ...tag,
      channelCount
    };
  });

  // Count channels with no tags
  const untaggedChannels = channels.filter(channel =>
    !channel.channel_tag_assignments || channel.channel_tag_assignments.length === 0
  ).length;

  // Calculate total unique channels
  const totalUniqueChannels = channels.length;

  // Calculate total channel-tag assignments
  const totalAssignments = channels.reduce((total, channel) => {
    return total + (channel.channel_tag_assignments?.length || 0);
  }, 0);

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Channel Tags</Text>
      <View style={styles.table}>
        {/* Header Row */}
        <View style={[styles.tableRow, styles.tableHeader]}>
          <View style={[styles.tableCell, { flex: 2 }]}>
            <Text style={styles.headerText}>Tag Name</Text>
          </View>
          {showTagDescription && (
            <View style={[styles.tableCell, { flex: 3 }]}>
              <Text style={styles.headerText}>Description</Text>
            </View>
          )}
          {showTagChannelCount && (
            <View style={[styles.tableCell, { flex: 1 }]}>
              <Text style={styles.headerText}>Channels</Text>
            </View>
          )}
        </View>

        {/* Tag Rows */}
        {tagChannelCounts.map(tag => (
          <View key={tag.id} style={styles.tableRow}>
            <View style={[styles.tableCell, { flex: 2 }]}>
              <Text style={{ fontWeight: 600 }}>{tag.name}</Text>
            </View>
            {showTagDescription && (
              <View style={[styles.tableCell, { flex: 3 }]}>
                <Text style={{ fontSize: 10, color: '#666' }}>
                  {tag.description || '-'}
                </Text>
              </View>
            )}
            {showTagChannelCount && (
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text>{tag.channelCount}</Text>
              </View>
            )}
          </View>
        ))}

        {/* Untagged channels row */}
        {untaggedChannels > 0 && (
          <View style={styles.tableRow}>
            <View style={[styles.tableCell, { flex: showTagDescription ? 5 : 2 }]}>
              <Text style={{ fontStyle: 'italic', color: '#666' }}>
                Untagged Channels
              </Text>
            </View>
            {showTagChannelCount && (
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text>{untaggedChannels}</Text>
              </View>
            )}
          </View>
        )}

        {/* Summary rows */}
        <View style={[styles.tableRow, { backgroundColor: '#f9fafb' }]}>
          <View style={[styles.tableCell, { flex: showTagDescription ? 5 : 2 }]}>
            <Text style={{ fontWeight: 700 }}>Total Tag Assignments</Text>
          </View>
          {showTagChannelCount && (
            <View style={[styles.tableCell, { flex: 1 }]}>
              <Text style={{ fontWeight: 700 }}>{totalAssignments}</Text>
            </View>
          )}
        </View>

        <View style={[styles.tableRow, { backgroundColor: '#f9fafb' }]}>
          <View style={[styles.tableCell, { flex: showTagDescription ? 5 : 2 }]}>
            <Text style={{ fontWeight: 700 }}>Total Unique Channels</Text>
          </View>
          {showTagChannelCount && (
            <View style={[styles.tableCell, { flex: 1 }]}>
              <Text style={{ fontWeight: 700 }}>{totalUniqueChannels}</Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}
